import { call, put, takeLatest } from 'redux-saga/effects';
import axios from 'axios';
import {
  fetchJewellersRequest,
  fetchJewellersSuccess,
  fetchJewellersFailure,
  fetchJewellersSidebarRequest,
  fetchJewellersSidebarSuccess,
  createJewellerRequest,
  createJewellerSuccess,
  createJewellerFailure,
  editJewellerRequest,
  editJewellerSuccess,
  editJewellerFailure,
  deleteJewellerRequest,
  deleteJewellerSuccess,
  deleteJewellerFailure,
  fetchInstalaxmiUsersRequest,
  fetchInstalaxmiUsersSuccess,
  fetchInstalaxmiUsersFailure,
} from '../JewellerList/JewellerSlice';
import { toast } from 'react-toastify';

function* fetchJewellers(action) {
  try {
    const { page = 0, limit = 10, search = '' } = action.payload || {};
    const response = yield call(
      axios.get,
      `${process.env.REACT_APP_API_BASE_URL}/api/v1/jeweller/JewellerList`,
      {
        params: {
          page: page + 1, // API is 1-based
          limit,
          search,
        },
      },
    );

    const jewellers = response?.data?.data?.enrichedJewellers ?? [];
    const total = response?.data?.data?.total || jewellers.length;

    const formattedJewellers = Array.isArray(jewellers)
      ? jewellers.map((jeweller, index) => ({
          id: jeweller._id || jeweller.jeweller?._id || index + 1,
          _id: jeweller._id,
          jewellerName: jeweller.jewellerName || '',
          email: jeweller.email || '',
          phone: jeweller.phone || '',
          countryCode: jeweller.countryCode || '',
          url: jeweller.url || '',
          package_name: jeweller.package_name || '',
          package_name_ios: jeweller.package_name_ios || '',
          Area: jeweller.address?.[0]?.Area || '',
          address1: jeweller.address?.[0]?.address1 || '',
          address2: jeweller.address?.[0]?.address2 || '',
          address3: jeweller.address?.[0]?.address3 || '',
          city: jeweller.address?.[0]?.city || '',
          state: jeweller.address?.[0]?.state || '',
          directors: jeweller.directors || [],
          signatories: jeweller.signatories || [],
          fullData: jeweller,
          createdBy: jeweller.createdBy || '',
          updatedBy: jeweller.updatedBy || '',
          comment: Array.isArray(jeweller.comment)
            ? jeweller.comment
            : jeweller.comment
            ? [jeweller.comment]
            : [],
        }))
      : [];

    yield put(fetchJewellersSuccess({ data: formattedJewellers, total }));
  } catch (error) {
    console.error('Error in fetchJewellers:', error);
    yield put(
      fetchJewellersFailure({
        message: error.response?.data?.message || error.message,
        type: 'error',
      }),
    );
  }
}

//  Fetch Jeweller dropdown for sidebar
function* fetchJewellersSidebar(action) {
  try {
    const response = yield call(() =>
      axios.get(
        `${
          process.env.REACT_APP_API_BASE_URL
        }/api/v1/jeweller/get-jewellers-list?allActive=${
          action?.payload?.allActive ?? false
        }`,
      ),
    );

    const jewellers = response?.data?.data ?? [];

    if (!Array.isArray(jewellers)) {
      console.warn('Expected array but got:', jewellers);
      return;
    }

    const formattedJewellers = jewellers.map((j, i) => ({
      id: j._id || i + 1, // Use backend _id if available
      jewellerName: j.jewellerName || '',
    }));

    yield put(fetchJewellersSidebarSuccess(formattedJewellers));
  } catch (error) {
    yield put(
      fetchJewellersFailure({
        message: error.response?.data?.message || error.message,
        type: 'error',
      }),
    );
  }
}

const sanitizeCommentField = (payload) => {
  const cleaned = { ...payload };
  const raw = cleaned.comment;
  let commentText = '';

  if (typeof raw === 'string') {
    commentText = raw.trim();
  } else if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    commentText = String(raw.comment || '').trim();
  }

  if (commentText) {
    cleaned.comment = commentText;
  } else {
    delete cleaned.comment;
  }

  return cleaned;
};

function* createJeweller({ payload }) {
  try {
    // Remove _id if present (important for creation)
    const { _id, ...restPayload } = payload;
    const cleanedPayload = sanitizeCommentField(restPayload);

    const response = yield call(
      axios.post,
      `${process.env.REACT_APP_API_BASE_URL}/api/v1/jeweller/createJeweller`,
      cleanedPayload,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
    const createdJeweller = response?.data?.jeweller;
    const message = response?.data?.message || 'Jeweller created successfully!';
    if (!response?.data?.success) {
      yield put(
        createJewellerFailure({
          message,
          type: 'error',
        }),
      );
      toast.error(message);
      return;
    }

    yield put(
      createJewellerSuccess({
        message,
        type: 'success',
        jeweller: createdJeweller,
      }),
    );
    toast.success(message);

    // Refresh list and dropdown
    yield put(fetchJewellersRequest({ page: 0, limit: 10 }));
    yield put(fetchJewellersSidebarRequest());
  } catch (error) {
    console.error(' Create Jeweller Error:', error);
    const errMessage =
      error?.response?.data?.message ||
      error.message ||
      "Failed to create Jeweller";

    yield put(
      createJewellerFailure({
        message:errMessage,
        type: 'error',
      }),
    );
    toast.error(errMessage);
  }
}

//  Update Jeweller
function* updateJeweller(action) {
  try {
    const { _id, ...data } = action.payload;

    const response = yield call(
      axios.patch,
      `${process.env.REACT_APP_API_BASE_URL}/api/v1/jeweller/updateJeweller/${_id}`,
      data,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
    yield put(editJewellerSuccess(response.data));
    if (response.data.success == false) {
      toast.error(response.data.message);
    } else {
      toast.success(response.data.message);
    }
    // Optionally refresh list
    yield put(fetchJewellersRequest({ page: 0, limit: 10 }));
  } catch (error) {
    yield put(
      editJewellerFailure(error.response?.data?.message || error.message),
    );
  }
}

//  Delete Jeweller
function* deleteJeweller(action) {
  try {
    const jewellerId = action.payload;
    yield call(
      axios.patch,
      `${process.env.REACT_APP_API_BASE_URL}/api/v1/jeweller/softDeleteJeweller/${jewellerId}`,
    );
    yield put(deleteJewellerSuccess(action.payload));
    yield put(fetchJewellersRequest());
    yield put(fetchJewellersSidebarRequest());
  } catch (error) {
    yield put(
      deleteJewellerFailure({
        message: error.response?.data?.message || error.message,
        type: 'error',
      }),
    );
  }
}

function* fetchInstalaxmiUsers() {
  try {
    const response = yield call(
      axios.get,
      `${process.env.REACT_APP_API_BASE_URL}/api/v1/users/getInstalaxmiUser`,
    );

    const users = Array.isArray(response?.data?.data) ? response.data.data : [];
    const formattedUsers = users.map((u) => ({
      _id: u._id,
      name: u.name || u.phone || "Unnamed",
    }));

    yield put(fetchInstalaxmiUsersSuccess(formattedUsers));
  } catch (error) {
    yield put(
      fetchInstalaxmiUsersFailure({
        message: error.response?.data?.message || error.message,
        type: 'error',
      }),
    );
  }
}

// Root Saga
export default function* JewellerSaga() {
  yield takeLatest(fetchJewellersRequest.type, fetchJewellers);
  yield takeLatest(fetchJewellersSidebarRequest.type, fetchJewellersSidebar);

  yield takeLatest(createJewellerRequest.type, createJeweller);
  yield takeLatest(editJewellerRequest.type, updateJeweller);
  yield takeLatest(deleteJewellerRequest.type, deleteJeweller);
  yield takeLatest(fetchInstalaxmiUsersRequest.type, fetchInstalaxmiUsers);
}
